// Breeding Planning — season-scoped plans and a reusable stud library.
//
// Two owner-scoped collections. Unlike horses, plans are NOT shared: a plan can
// reference an outside mare and an outside stud, and the fees/contracts on it
// are the owner's business. The one fact worth sharing (a signed cross) is
// mirrored onto the horse document in a later phase, not read from here.
//
// A plan is one mare for one season. Confirming a stud spins off a NEW plan for
// that cross and leaves the original short list standing with the remaining
// candidates, so a mare can be committed to one stallion while still weighing
// another for a second cross the same year.
import {
  getDocs,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './config';
import { fetchActiveSeasonSharesBy, seasonShareCovers, shareEntryFor } from './planShares';

const plansRef = collection(db, 'breedingPlans');
const studsRef = collection(db, 'studs');

export const PLAN_STAGES = ['shortlist', 'confirmed', 'signed'];
export const STAGE_LABELS = {
  shortlist: 'Short List',
  confirmed: 'Confirmed',
  signed: 'Signed Contract',
};

// Live list of the caller's plans, newest season first then mare name.
export const subscribeToBreedingPlans = (uid, callback) => {
  if (!uid) { callback([]); return () => {}; }
  const byOwner = [];
  const byMember = [];
  const emit = () => {
    const seen = new Map();
    [...byOwner, ...byMember].forEach((p) => seen.set(p.id, p));
    const items = [...seen.values()];
    items.sort((a, b) =>
      (b.year || 0) - (a.year || 0)
      || String(a.mareName || '').localeCompare(String(b.mareName || ''))
    );
    callback(items);
  };
  const unsubA = onSnapshot(query(plansRef, where('ownerId', '==', uid)), (snap) => {
    byOwner.length = 0;
    snap.docs.forEach((d) => byOwner.push({ id: d.id, ...d.data() }));
    emit();
  }, () => {});
  const unsubB = onSnapshot(query(plansRef, where('members', 'array-contains', uid)), (snap) => {
    byMember.length = 0;
    snap.docs.forEach((d) => byMember.push({ id: d.id, ...d.data() }));
    emit();
  }, () => {});
  return () => { unsubA(); unsubB(); };
};

// Who may change a plan: its owner, or someone given Full Access.
export const canEditPlan = (plan, uid) =>
  !!uid && !!plan && (plan.ownerId === uid || (plan.editors || []).includes(uid));

// Share a plan. 'full' can edit it; 'view' can only read. `extra` tags the
// entry with where it came from (a season share), so ending that share takes
// back only what it gave and leaves a plan shared by hand alone.
export const shareBreedingPlan = (planId, currentShares, toUid, toEmail, toName, permission, extra = {}) =>
  updateDoc(doc(db, 'breedingPlans', planId), {
    members: arrayUnion(toUid),
    ...(permission === 'full' ? { editors: arrayUnion(toUid) } : { editors: arrayRemove(toUid) }),
    shares: [
      ...currentShares.filter((x) => x.uid !== toUid),
      { uid: toUid, email: toEmail, name: toName || toEmail, permission, ...extra },
    ],
    updatedAt: serverTimestamp(),
  });

export const revokeBreedingPlanShare = (planId, currentShares, uid) =>
  updateDoc(doc(db, 'breedingPlans', planId), {
    members: arrayRemove(uid),
    editors: arrayRemove(uid),
    shares: currentShares.filter((x) => x.uid !== uid),
    updatedAt: serverTimestamp(),
  });

// Only the mare is required. Everything else is asked for by the stage that
// needs it, so a short list can be three names jotted after a conversation.
export const addBreedingPlan = async (uid, data) => {
  // A season share the owner has already given reaches this plan from birth —
  // that is what makes "share my 2027 plans" mean the ones added next month
  // too. One entry per person: someone covered by both a year share and an
  // every-season share gets the higher level.
  const entries = new Map();
  try {
    const active = await fetchActiveSeasonSharesBy(uid);
    active
      .filter((s) => seasonShareCovers(s, { ownerId: uid, year: data.year }))
      .forEach((s) => {
        const e = shareEntryFor(s);
        const prev = entries.get(e.uid);
        if (!prev || (prev.permission !== 'full' && e.permission === 'full')) entries.set(e.uid, e);
      });
  } catch (e) { /* the plan is still created; the owner's sync catches up */ }
  const shares = [...entries.values()];
  return addDoc(plansRef, {
    ownerId: uid,
    year: data.year,
    stage: data.stage || 'shortlist',
    mareName: (data.mareName || '').trim(),
    mareId: data.mareId || null,
    studs: data.studs || [],
    chosenStudId: data.chosenStudId || null,
    members: [uid, ...shares.map((s) => s.uid)],
    editors: shares.filter((s) => s.permission === 'full').map((s) => s.uid),
    shares,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

// Every plan an owner has, fetched once — for the owner's device to apply or
// take back a season share without holding a live subscription open.
export const fetchMyBreedingPlans = async (uid) => {
  if (!uid) return [];
  const snap = await getDocs(query(plansRef, where('ownerId', '==', uid)));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

export const updateBreedingPlan = (planId, data) =>
  updateDoc(doc(db, 'breedingPlans', planId), { ...data, updatedAt: serverTimestamp() });

export const deleteBreedingPlan = (planId) => deleteDoc(doc(db, 'breedingPlans', planId));

// Live stud library for the caller, alphabetical.
export const subscribeToStuds = (uid, callback) => {
  if (!uid) { callback([]); return () => {}; }
  const q = query(studsRef, where('ownerId', '==', uid));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    items.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
    callback(items);
  }, () => { /* keep last-known state on transient error */ });
};

// A name is the only required field. Fee, farm, contact and the two-generation
// pedigree fill in over time as the cross gets more serious.
export const addStud = (uid, data) =>
  addDoc(studsRef, {
    ownerId: uid,
    name: (data.name || '').trim(),
    fee: typeof data.fee === 'number' ? data.fee : null,
    farm: (data.farm || '').trim(),
    contact: (data.contact || '').trim(),
    horseId: data.horseId || null,   // set when the stallion is in the herd
    sire: (data.sire || '').trim(),
    sireSire: (data.sireSire || '').trim(),
    sireDam: (data.sireDam || '').trim(),
    dam: (data.dam || '').trim(),
    damSire: (data.damSire || '').trim(),
    damDam: (data.damDam || '').trim(),
    // Programs he is nominated to, kept as a running list the owner types.
    // Briefly stored as an array — join one back into text so nothing is lost.
    incentives: Array.isArray(data.incentives)
      ? data.incentives.join('\n')
      : (data.incentives || '').trim(),
    notes: (data.notes || '').trim(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// Editing the library changes what NEW plans quote. Plans keep the fee they
// were created with, so last year's signed contract is never rewritten.
export const updateStud = (studId, data) =>
  updateDoc(doc(db, 'studs', studId), { ...data, updatedAt: serverTimestamp() });

export const deleteStud = (studId) => deleteDoc(doc(db, 'studs', studId));

// Live single plan — the contract screen subscribes to one document so a
// payment or a ticked check lands without re-reading the whole season.
export const subscribeToBreedingPlan = (planId, callback) => {
  if (!planId) { callback(null); return () => {}; }
  return onSnapshot(doc(db, 'breedingPlans', planId), (snap) => {
    callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
  }, () => { /* keep last-known state on transient error */ });
};

// The pregnancy checks, generated from the bred date rather than ticked in as
// they happen. A recip carries the extra 120 day check, matching what the horse
// record already does for a recip pregnancy.
export const BREEDING_CHECKS = [
  { id: 'c14', name: '14 Day Check', daysOffset: 14 },
  { id: 'c30', name: '30 Day Check', daysOffset: 30 },
  { id: 'c45', name: '45 Day Check', daysOffset: 45 },
];
export const RECIP_CHECK = { id: 'c120', name: '120 Day Check', daysOffset: 120 };

export const buildChecks = (isRecip) =>
  [...BREEDING_CHECKS, ...(isRecip ? [RECIP_CHECK] : [])].map((c) => ({ ...c, completed: false }));
